/// <reference types="google.maps" />

export interface LatLng {
  lat: number;
  lng: number;
}

export interface AutocompletionRequest {
  location?: LatLng;
  radius?: number;
}

// Same shape the legacy AutocompleteService returned, so stored values and consumers stay unchanged.
export interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export interface PlaceOption {
  label: string;
  value: PlacePrediction;
}

export const buildAutocompleteRequest = (
  autocompletionRequest: AutocompletionRequest,
  input: string
): google.maps.places.AutocompleteRequest => {
  const { location, radius } = autocompletionRequest;
  const request: google.maps.places.AutocompleteRequest = { input };

  // The legacy `location` + `radius` pair maps to a circular location bias.
  if (location && radius) {
    request.locationBias = { center: location, radius };
  } else if (location) {
    request.locationBias = location;
  }

  return request;
};

export const toPlaceOption = (prediction: google.maps.places.PlacePrediction): PlaceOption => {
  const description = prediction.text.text;

  return {
    label: description,
    value: {
      description,
      place_id: prediction.placeId,
      structured_formatting: {
        main_text: prediction.mainText?.text ?? description,
        secondary_text: prediction.secondaryText?.text ?? ""
      }
    }
  };
};

// Fetches place suggestions through AutocompleteSuggestion, which replaces the legacy AutocompleteService.
export const fetchPlacePredictions = async (
  autocompletionRequest: AutocompletionRequest,
  input: string
): Promise<PlaceOption[]> => {
  try {
    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
      buildAutocompleteRequest(autocompletionRequest, input)
    );

    return suggestions
      .map(suggestion => suggestion.placePrediction)
      .filter((prediction): prediction is google.maps.places.PlacePrediction => Boolean(prediction))
      .map(toPlaceOption);
  } catch (error) {
    // The legacy service answered failures and "no results" with an empty list; keep the same behaviour.
    console.error("[GooglePlacesAutocomplete] Could not fetch place suggestions", error);
    return [];
  }
};
